import { Inject, Injectable } from 'vue-typedi';

import Caching from './Caching';
import { CLIENT } from './TransportService';

const optionsByDef = {
	useCache: false
};

@Injectable()
export default class RestApi<TBaseType> extends Caching {
	@Inject(CLIENT)
	protected client!: TRA.IBaseTransportService;

	constructor(baseUrl: RA.BaseUrl, serverUrl: Common.StrNull = null) {
		super();
		this.baseUrl = baseUrl;
		this.serverUrl = serverUrl;
	}

	private baseUrl: RA.BaseUrl;
	private serverUrl: Common.StrNull;

	private get fullUrl(): string {
		const { baseApiUrl } = this.client;
		const baseUrl = typeof this.baseUrl === 'function' ? this.baseUrl() : this.baseUrl;
		const fullUrl = baseUrl ? `${baseApiUrl}/${baseUrl}` : baseApiUrl;

		return this.serverUrl ? `${this.serverUrl}${fullUrl}` : fullUrl;
	}

	private getItemUrl(id: string | number): string {
		return `${this.fullUrl}/${id}`;
	}

	private getEntityUrl({ id, name, prefix: _prefix }: RA.IEntityRest): string {
		const prefix = _prefix ? `${_prefix}/` : '';

		return `${this.fullUrl}/${prefix}${id}/${name}`;
	}

	private getUrl(path: Common.StrNull): string {
		return `${this.fullUrl}/${path}`;
	}

	private getOptionsByDef(options?: RA.IOptionsRestApi): RA.IOptionsRestApi {
		const opts: RA.IOptionsRestApi = options || { useCache: optionsByDef.useCache };

		if (opts.useCache === undefined) {
			opts.useCache = optionsByDef.useCache;
		}

		return opts;
	}

	protected getOptions(options?: RA.IOptionsRestApi, config?: RA.IConfigOptionsRestApi): RA.IOptionsRestApi {
		return super.getOptions(this.getOptionsByDef(options), config);
	}

	private async callRequest<TResponse = TRA.CommonResponse>(
		request: () => Promise<TRA.IResponse<TResponse>>,
		options: RA.IOptionsRestApi
	): Promise<TRA.IResponse<TResponse>> {
		let response;

		try {
			response = await this.doRequest<TResponse>(request, options);

			return response;
		} catch (e) {
			response = e as TRA.IResponse<TResponse>;
			response.isError = true;

			throw response;
		}
	}

	public setServerUrl(serverUrl: Common.StrNull): void {
		this.serverUrl = serverUrl;
	}

	/**
	 * Get list
	 * @param options RA.IOptionsRestApi
	 * @method get
	 * @example ​/<baseApiUrl>​/<baseUrl>
	 */
	public async getList<TResponse = TBaseType>(
		options?: RA.IOptionsRestApi
	): Promise<TRA.IResponse<RA.IResponseList<TResponse>>> {
		const url = this.fullUrl;
		const opts = this.getOptions(options, { cacheable: true, url });
		const request = (): Promise<TRA.IResponse<RA.IResponseList<TResponse>>> =>
			this.client.get<RA.IResponseList<TResponse>>(url, opts.config);

		return this.callRequest<RA.IResponseList<TResponse>>(request, opts);
	}

	/**
	 * Get list with path
	 * @param path Location of request
	 * @param options
	 * @method get
	 * @example /<baseApiUrl>​/<baseUrl>/{path}
	 */
	public async getListWithPath<TResponse = TBaseType>(
		path: Common.StrNull = null,
		options?: RA.IOptionsRestApi
	): Promise<TRA.IResponse<RA.IResponseList<TResponse>>> {
		const url = this.getUrl(path);
		const opts = this.getOptions(options, { cacheable: true, url });
		const request = (): Promise<TRA.IResponse<RA.IResponseList<TResponse>>> =>
			this.client.get<RA.IResponseList<TResponse>>(url, opts.config);

		return this.callRequest<RA.IResponseList<TResponse>>(request, opts);
	}

	/**
	 * Get list by id and name
	 * @param entity RA.IEntityRest
	 * @param options RA.IOptionsRestApi
	 * @method get
	 * @example ​/<baseApiUrl>​/<baseUrl>/{id}​/{name}
	 */
	public async getListByEntity<TResponse = TBaseType>(
		entity: RA.IEntityRest,
		options?: RA.IOptionsRestApi
	): Promise<TRA.IResponse<RA.IResponseList<TResponse>>> {
		const url = this.getEntityUrl(entity);
		const opts = this.getOptions(options, { cacheable: true, url });
		const request = (): Promise<TRA.IResponse<RA.IResponseList<TResponse>>> =>
			this.client.get<RA.IResponseList<TResponse>>(url, opts.config);

		return this.callRequest<RA.IResponseList<TResponse>>(request, opts);
	}

	/**
	 * Get entity by id
	 * @param id
	 * @param options RA.IOptionsRestApi
	 * @method get
	 * @example /<baseApiUrl>​/<baseUrl>/{id}
	 */
	public async getById<TResponse = TBaseType>(
		id: string | number,
		options?: RA.IOptionsRestApi
	): Promise<TRA.IResponse<TResponse>> {
		const url = this.getItemUrl(id);
		const opts = this.getOptions(options, { cacheable: true, url });
		const request = (): Promise<TRA.IResponse<TResponse>> => this.client.get<TResponse>(url, opts.config);

		return this.callRequest<TResponse>(request, opts);
	}

	/**
	 * Get entity by id and name
	 * @param entity RA.IEntityRest
	 * @param options RA.IOptionsRestApi
	 * @method get
	 * @example /<baseApiUrl>​/<baseUrl>/{id}/{name}
	 */
	public async getEntityById<TResponse = TBaseType>(
		entity: RA.IEntityRest,
		options?: RA.IOptionsRestApi
	): Promise<TRA.IResponse<TResponse>> {
		const url = this.getEntityUrl(entity);
		const opts = this.getOptions(options, { cacheable: true, url });
		const request = (): Promise<TRA.IResponse<TResponse>> => this.client.get<TResponse>(url, opts.config);

		return this.callRequest<TResponse>(request, opts);
	}

	/**
	 * Get entity
	 * @param options RA.IOptionsRestApi
	 * @method get
	 * @example /<baseApiUrl>​/<baseUrl>
	 */
	public async get<TResponse = TBaseType>(options?: RA.IOptionsRestApi): Promise<TRA.IResponse<TResponse>> {
		const url = this.fullUrl;
		const opts = this.getOptions(options, { cacheable: true, url });
		const request = (): Promise<TRA.IResponse<TResponse>> => this.client.get<TResponse>(url, opts.config);

		return this.callRequest<TResponse>(request, opts);
	}

	/**
	 * Get entity
	 * @param url string
	 * @param options RA.IOptionsRestApi
	 * @method get
	 * @example /<any Url>
	 */
	public async getByUrl<TResponse = TBaseType>(
		url: string,
		options?: RA.IOptionsRestApi
	): Promise<TRA.IResponse<TResponse>> {
		const opts = this.getOptions(options, { cacheable: true, url });
		const request = (): Promise<TRA.IResponse<TResponse>> => this.client.get<TResponse>(url, opts.config);

		return this.callRequest<TResponse>(request, opts);
	}

	/**
	 * Get entity with location
	 * @param path Location of request
	 * @param options RA.IOptionsRestApi
	 * @method get
	 * @example /<baseApiUrl>​/<baseUrl>/{path}
	 */
	public async getByPath<TResponse = TBaseType>(
		path: string,
		options?: RA.IOptionsRestApi
	): Promise<TRA.IResponse<TResponse>> {
		const url = this.getUrl(path);
		const opts = this.getOptions(options, { cacheable: true, url });
		const request = (): Promise<TRA.IResponse<TResponse>> => this.client.get<TResponse>(url, opts.config);

		return this.callRequest<TResponse>(request, opts);
	}

	/**
	 * Get entity with body
	 * @param body TBody
	 * @param options RA.IOptionsRestApi
	 * @method post
	 * @example /<baseApiUrl>​/<baseUrl>
	 */
	public async getWithBody<TResponse = TBaseType, TBody = TResponse>(
		body: TBody,
		options?: RA.IOptionsRestApi
	): Promise<TRA.IResponse<TResponse>> {
		const url = this.fullUrl;
		const opts = this.getOptions(options, { url, body: body as Common.IMap<never> | undefined, cacheable: true });
		const request = (): Promise<TRA.IResponse<TResponse>> =>
			this.client.post<TResponse, TBody>(url, body, opts.config);

		return this.callRequest<TResponse>(request, opts);
	}

	/**
	 * Get entity with body by id
	 * @param id
	 * @param body TBody
	 * @param options RA.IOptionsRestApi
	 * @method post
	 * @example /<baseApiUrl>​/<baseUrl>/{id}
	 */
	public async getWithBodyById<TResponse = TBaseType, TBody = TResponse>(
		id: string,
		body: TBody,
		options?: RA.IOptionsRestApi
	): Promise<TRA.IResponse<TResponse>> {
		const url = this.getItemUrl(id);
		const opts = this.getOptions(options, { url, body: body as Common.IMap<never> | undefined, cacheable: true });
		const request = (): Promise<TRA.IResponse<TResponse>> =>
			this.client.post<TResponse, TBody>(url, body, opts.config);

		return this.callRequest<TResponse>(request, opts);
	}

	/**
	 * Get entity with body by id and name
	 * @param entity RA.IEntityRest
	 * @param body TBody
	 * @param options RA.IOptionsRestApi
	 * @method post
	 * @example /<baseApiUrl>​/<baseUrl>/{id}/{name}
	 */
	public async getWithBodyByEntity<TResponse = TBaseType, TBody = TResponse>(
		entity: RA.IEntityRest,
		body: TBody,
		options?: RA.IOptionsRestApi
	): Promise<TRA.IResponse<TResponse>> {
		const url = this.getEntityUrl(entity);
		const opts = this.getOptions(options, { url, body: body as Common.IMap<never> | undefined, cacheable: true });
		const request = (): Promise<TRA.IResponse<TResponse>> =>
			this.client.post<TResponse, TBody>(url, body, opts.config);

		return this.callRequest<TResponse>(request, opts);
	}

	/**
	 * Create entity
	 * @param body TBody
	 * @param options RA.IOptionsRestApi
	 * @method post
	 * @example /<baseApiUrl>​/<baseUrl>
	 */
	public async create<TResponse = TBaseType, TBody = TResponse>(
		body: TBody,
		options?: RA.IOptionsRestApi
	): Promise<TRA.IResponse<TResponse>> {
		const url = this.fullUrl;
		const opts = this.getOptions(options);
		const request = (): Promise<TRA.IResponse<TResponse>> =>
			this.client.post<TResponse, TBody>(url, body, opts.config);

		return this.callRequest<TResponse>(request, this.getOptions(options));
	}

	/**
	 * Create entity by path without body
	 * @param path
	 * @param body TBody
	 * @param options RA.IOptionsRestApi
	 * @method post
	 * @example /<baseApiUrl>​/<baseUrl>/{path}
	 */
	public async createByPath<TResponse = TBaseType, TBody = TResponse>(
		path: string,
		body: TBody,
		options?: RA.IOptionsRestApi
	): Promise<TRA.IResponse<TResponse>> {
		const url = this.getUrl(path);
		const opts = this.getOptions(options);
		const request = (): Promise<TRA.IResponse<TResponse>> =>
			this.client.post<TResponse, TBody>(url, body, opts.config);

		return this.callRequest<TResponse>(request, this.getOptions(options));
	}

	/**
	 * Create entity by id and name
	 * @param entity RA.IEntityRest
	 * @param body TBody
	 * @param options RA.IOptionsRestApi
	 * @method post
	 * @example /<baseApiUrl>​/<baseUrl>/{id}/{name}
	 */
	public async createByEntity<TResponse = TBaseType, TBody = TResponse>(
		entity: RA.IEntityRest,
		body: TBody,
		options?: RA.IOptionsRestApi
	): Promise<TRA.IResponse<TResponse>> {
		const url = this.getEntityUrl(entity);
		const opts = this.getOptions(options);
		const request = (): Promise<TRA.IResponse<TResponse>> =>
			this.client.post<TResponse, TBody>(url, body, opts.config);

		return this.callRequest<TResponse>(request, this.getOptions(options));
	}

	/**
	 * Update entity
	 * @param body TBody
	 * @param options RA.IOptionsRestApi
	 * @method patch
	 * @example /<baseApiUrl>​/<baseUrl>
	 */
	public async update<TResponse = TBaseType, TBody = TResponse>(
		body: TBody,
		options?: RA.IOptionsRestApi
	): Promise<TRA.IResponse<TResponse>> {
		const url = this.fullUrl;
		const opts = this.getOptions(options);
		const request = (): Promise<TRA.IResponse<TResponse>> =>
			this.client.patch<TResponse, TBody>(url, body, opts.config);

		return this.callRequest<TResponse>(request, this.getOptions(options));
	}

	/**
	 * Update entity by id
	 * @param id
	 * @param body TBody
	 * @param options RA.IOptionsRestApi
	 * @method patch
	 * @example /<baseApiUrl>​/<baseUrl>/{id}
	 */
	public async updateById<TResponse = TBaseType, TBody = TResponse>(
		id: string,
		body: TBody,
		options?: RA.IOptionsRestApi
	): Promise<TRA.IResponse<TResponse>> {
		const url = this.getItemUrl(id);
		const opts = this.getOptions(options);
		const request = (): Promise<TRA.IResponse<TResponse>> =>
			this.client.patch<TResponse, TBody>(url, body, opts.config);

		return this.callRequest<TResponse>(request, this.getOptions(options));
	}

	/**
	 * Replace entity
	 * @param body TBody
	 * @param options RA.IOptionsRestApi
	 * @method put
	 * @example /<baseApiUrl>​/<baseUrl>
	 */
	public async replace<TResponse = TBaseType, TBody = TResponse>(
		body: TBody,
		options?: RA.IOptionsRestApi
	): Promise<TRA.IResponse<TResponse>> {
		const url = this.fullUrl;
		const opts = this.getOptions(options);
		const request = (): Promise<TRA.IResponse<TResponse>> =>
			this.client.put<TResponse, TBody>(url, body, opts.config);

		return this.callRequest<TResponse>(request, this.getOptions(options));
	}

	/**
	 * Replace entity by path
	 * @param path Location of request
	 * @param body TBody
	 * @param options RA.IOptionsRestApi
	 * @method put
	 * @example /<baseApiUrl>​/<baseUrl>/{path}
	 */
	public async replaceByPath<TResponse = TBaseType, TBody = TResponse>(
		path: string,
		body: TBody,
		options?: RA.IOptionsRestApi
	): Promise<TRA.IResponse<TResponse>> {
		const url = this.getItemUrl(path);
		const opts = this.getOptions(options);
		const request = (): Promise<TRA.IResponse<TResponse>> =>
			this.client.put<TResponse, TBody>(url, body, opts.config);

		return this.callRequest<TResponse>(request, this.getOptions(options));
	}

	/**
	 * Replace entity by id
	 * @param id
	 * @param body TBody
	 * @param options RA.IOptionsRestApi
	 * @method put
	 * @example /<baseApiUrl>​/<baseUrl>/{id}
	 */
	public async replaceById<TResponse = TBaseType, TBody = TResponse>(
		id: string | number,
		body: TBody,
		options?: RA.IOptionsRestApi
	): Promise<TRA.IResponse<TResponse>> {
		const url = this.getItemUrl(id);
		const opts = this.getOptions(options);
		const request = (): Promise<TRA.IResponse<TResponse>> =>
			this.client.put<TResponse, TBody>(url, body, opts.config);

		return this.callRequest<TResponse>(request, this.getOptions(options));
	}

	/**
	 * Delete entity by id
	 * @param id
	 * @param options RA.IOptionsRestApi
	 * @method delete
	 * @example /<baseApiUrl>​/<baseUrl>/{id}
	 */
	public async delete<TResponse = TBaseType>(
		id: string | number,
		options?: RA.IOptionsRestApi
	): Promise<TRA.IResponse<TResponse>> {
		const url = this.getItemUrl(id);
		const opts = this.getOptions(options);
		const request = (): Promise<TRA.IResponse<TResponse>> => this.client.delete<TResponse>(url, opts.config);

		return this.callRequest<TResponse>(request, this.getOptions(options));
	}

	/**
	 * Delete entity by id and name
	 * @param entity RA.IEntityRest
	 * @param options RA.IOptionsRestApi
	 * @method delete
	 * @example /<baseApiUrl>​/<baseUrl>/{id}/{name}
	 */
	public async deleteByEntity<TResponse = TBaseType>(
		entity: RA.IEntityRest,
		options?: RA.IOptionsRestApi
	): Promise<TRA.IResponse<TResponse>> {
		const url = this.getEntityUrl(entity);
		const opts = this.getOptions(options);
		const request = (): Promise<TRA.IResponse<TResponse>> => this.client.delete<TResponse>(url, opts.config);

		return this.callRequest<TResponse>(request, this.getOptions(options));
	}
}
